import React from 'react';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import getCountryCodes from '../../../translations/countryCodes';

import { Form, FieldSelect, FieldTextInput, PrimaryButton, Heading } from '../../../components';

import css from './BusinessOnboardingModal.module.css';

const BusinessOnboardingModalComponent = props => {
  const intl = useIntl();
  const { className, rootClassName, locale, inProgress, onSubmit, error, currentUser } = props;
  const classes = classNames(rootClassName || css.root, className);

  const originAddress = currentUser?.attributes?.profile?.publicData?.originAddress || {};
  const initialValues = { ...originAddress, country: originAddress.country || 'GB' };

  const countryCodes = getCountryCodes(locale).filter(c => c.code === 'GB');

  const requiredMessage = id => intl.formatMessage({ id });
  const requiredString = msg => validators.required(msg);

  const handleOnSubmit = values => {
    const { phone, ...rest } = values;
    const trimmedPhone = phone ? phone.replace(/\s+/g, '').replace(/^(\+44|0)/, '') : phone;
    onSubmit({ ...rest, phone: trimmedPhone });
  };

  return (
    <div className={classes}>
      <Heading as="h2" rootClassName={css.heading}>
        <FormattedMessage id="BusinessOnboardingModal.title" />
      </Heading>
      <p className={css.subtitle}>
        <FormattedMessage id="BusinessOnboardingModal.subtitle" />
      </p>

      {error ? (
        <div className={css.error}>
          <FormattedMessage id="BusinessOnboardingModal.saveFailed" />
        </div>
      ) : null}

      <FinalForm
        onSubmit={handleOnSubmit}
        initialValues={initialValues}
        render={formRenderProps => {
          const { handleSubmit, invalid } = formRenderProps;
          const submitDisabled = invalid || inProgress;

          return (
            <Form className={css.form} onSubmit={handleSubmit}>
              <div className={css.row}>
                <FieldTextInput
                  id="origin.companyName"
                  name="companyName"
                  type="text"
                  label={intl.formatMessage({ id: 'BusinessOnboardingModal.companyNameLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'BusinessOnboardingModal.companyNamePlaceholder',
                  })}
                  validate={validators.composeValidators(
                    requiredString(requiredMessage('BusinessOnboardingModal.companyNameRequired')),
                    validators.maxLength(
                      intl.formatMessage({ id: 'BusinessOnboardingModal.companyNameTooLong' }),
                      15
                    )
                  )}
                />
                <FieldTextInput
                  id="origin.contactName"
                  name="contactName"
                  type="text"
                  label={intl.formatMessage({ id: 'BusinessOnboardingModal.contactNameLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'BusinessOnboardingModal.contactNamePlaceholder',
                  })}
                  validate={validators.composeValidators(
                    requiredString(requiredMessage('BusinessOnboardingModal.contactNameRequired')),
                    validators.maxLength(
                      intl.formatMessage({ id: 'BusinessOnboardingModal.contactNameTooLong' }),
                      15
                    )
                  )}
                />
              </div>

              <div className={css.row}>
                <FieldTextInput
                  id="origin.email"
                  name="email"
                  type="email"
                  label={intl.formatMessage({ id: 'BusinessOnboardingModal.emailLabel' })}
                  placeholder={intl.formatMessage({ id: 'BusinessOnboardingModal.emailPlaceholder' })}
                  validate={validators.composeValidators(
                    requiredString(requiredMessage('BusinessOnboardingModal.emailRequired')),
                    validators.emailFormatValid(
                      intl.formatMessage({ id: 'BusinessOnboardingModal.emailInvalid' })
                    )
                  )}
                />
                <FieldTextInput
                  id="origin.phone"
                  name="phone"
                  type="text"
                  label={intl.formatMessage({ id: 'BusinessOnboardingModal.phoneLabel' })}
                  placeholder={intl.formatMessage({ id: 'BusinessOnboardingModal.phonePlaceholder' })}
                  prefix="+44"
                  maxLength={10}
                  parse={value => {
                    if (!value) return value;
                    let digits = value.replace(/\D/g, '');
                    if (digits.startsWith('44')) digits = digits.slice(2);
                    if (digits.startsWith('0')) digits = digits.slice(1);
                    return digits.slice(0, 10);
                  }}
                  validate={validators.composeValidators(
                    requiredString(requiredMessage('BusinessOnboardingModal.phoneRequired')),
                    validators.ukPhoneNumberFormatValid(
                      intl.formatMessage({ id: 'BusinessOnboardingModal.phoneInvalid' })
                    )
                  )}
                />
              </div>

              <div className={css.row}>
                <FieldTextInput
                  id="origin.street"
                  name="street"
                  type="text"
                  label={intl.formatMessage({ id: 'BusinessOnboardingModal.streetLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'BusinessOnboardingModal.streetPlaceholder',
                  })}
                  validate={requiredString(requiredMessage('BusinessOnboardingModal.streetRequired'))}
                />
                <FieldTextInput
                  id="origin.houseNumber"
                  name="houseNumber"
                  type="textarea"
                  label={intl.formatMessage({ id: 'BusinessOnboardingModal.houseNumberLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'BusinessOnboardingModal.houseNumberPlaceholder',
                  })}
                  validate={requiredString(
                    requiredMessage('BusinessOnboardingModal.houseNumberRequired')
                  )}
                />
              </div>

              <FieldTextInput
                id="origin.addressLine2"
                name="addressLine2"
                type="textarea"
                label={intl.formatMessage({ id: 'BusinessOnboardingModal.addressLine2Label' })}
                placeholder={intl.formatMessage({
                  id: 'BusinessOnboardingModal.addressLine2Placeholder',
                })}
              />

              <div className={css.row}>
                <FieldTextInput
                  id="origin.postalCode"
                  name="postalCode"
                  type="text"
                  label={intl.formatMessage({ id: 'BusinessOnboardingModal.postalCodeLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'BusinessOnboardingModal.postalCodePlaceholder',
                  })}
                  validate={validators.composeValidators(
                    requiredString(requiredMessage('BusinessOnboardingModal.postalCodeRequired')),
                    validators.ukPostalCodeFormatValid(
                      intl.formatMessage({ id: 'BusinessOnboardingModal.postalCodeInvalid' })
                    )
                  )}
                />
                <FieldTextInput
                  id="origin.city"
                  name="city"
                  type="text"
                  label={intl.formatMessage({ id: 'BusinessOnboardingModal.cityLabel' })}
                  placeholder={intl.formatMessage({ id: 'BusinessOnboardingModal.cityPlaceholder' })}
                  validate={requiredString(requiredMessage('BusinessOnboardingModal.cityRequired'))}
                />
              </div>

              <div className={css.row}>
                <FieldTextInput
                  id="origin.state"
                  name="state"
                  type="text"
                  label={intl.formatMessage({ id: 'BusinessOnboardingModal.stateLabel' })}
                  placeholder={intl.formatMessage({
                    id: 'BusinessOnboardingModal.statePlaceholder',
                  })}
                />

                <FieldSelect
                  id="origin.country"
                  name="country"
                  label={intl.formatMessage({ id: 'BusinessOnboardingModal.countryLabel' })}
                  validate={requiredString(
                    requiredMessage('BusinessOnboardingModal.countryRequired')
                  )}
                >
                  <option disabled value="">
                    {intl.formatMessage({ id: 'BusinessOnboardingModal.countryPlaceholder' })}
                  </option>
                  {countryCodes.map(country => (
                    <option key={country.code} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </FieldSelect>
              </div>

              <div className={css.actions}>
                <PrimaryButton type="submit" inProgress={inProgress} disabled={submitDisabled}>
                  <FormattedMessage id="BusinessOnboardingModal.save" />
                </PrimaryButton>
              </div>
            </Form>
          );
        }}
      />
    </div>
  );
};

export default BusinessOnboardingModalComponent;

